import { makeAutoObservable } from 'mobx';
import { ProfileV1 } from '@fshr/microservices/profile';
import { getConfig } from '@fshr/shared-react/config';
import { UserPanelTabId } from '@fshr/shared/types';
import { GlobalSettingsFeatureFlags } from '@fshr/microservices/compiled/content.v1';

export type GlobalStoreType = {
	profile?: ProfileV1.ProfileSummaryResponse;
	ppProfile?: ProfileV1.PpProfileResponse;
	ppMode?: boolean;
	activeBookingId?: string;
	preferredCurrency: string;
	currentProperty: string | null;
	currentConfirmationNumber: string | null;
	fetching: boolean;
	error: boolean;
	webChatEnabled: boolean;
	userPanelOpenTab: UserPanelTabId | null;
	featureFlags: GlobalSettingsFeatureFlags;
	readonly userHasItinerary: boolean;
};

class GlobalStore implements GlobalStoreType {
	profile?: ProfileV1.ProfileSummaryResponse;
	ppProfile?: ProfileV1.PpProfileResponse;
	ppMode?: boolean;
	activeBookingId?: string;
	preferredCurrency: string;
	currentProperty: string | null;
	currentConfirmationNumber: string | null;
	fetching: boolean;
	error: boolean;
	webChatEnabled: boolean;
	userPanelOpenTab: UserPanelTabId | null;
	featureFlags: GlobalSettingsFeatureFlags;

	constructor(owsCode?: string, confirmationNumber?: string) {
		this.profile = undefined;
		this.ppProfile = undefined;
		this.ppMode =
			window &&
			(window.location.pathname.includes('/preferredpartner') ||
				!!window.FS?.['globalInfo']?.ppMode ||
				window.location.search.includes('ppMode=true'));
		this.activeBookingId = undefined;
		this.preferredCurrency = '';
		this.currentProperty = owsCode || null;
		this.currentConfirmationNumber = confirmationNumber || null;
		this.fetching = true;
		this.error = false;
		this.webChatEnabled = false;
		this.userPanelOpenTab = null;
		this.featureFlags = {
			enableProfileOnWww: false,
			enableItineraryOverlay: false,
			enableRecommendationEngine: false,
			enableSignatureExperiences: false,
			enableRecommendedAncillaryList: false,
			enableAnonGuestRecommedation: false,
		};
		makeAutoObservable(this);

		//do the initial fetch
		if (typeof window !== 'undefined') {
			this.refreshState(owsCode ? { propertyOwsCode: owsCode || '' } : undefined);
		}

		//check if we should automatically open the UserPanel
		const urlHash = window?.location.hash?.replace('#', '');
		if (urlHash && Object.values(UserPanelTabId).find(id => id === urlHash)) {
			this.setUserPanelOpenTab(urlHash as UserPanelTabId);
		}
	}

	async setCurrentProperty(property: string | null) {
		if (property !== this.currentProperty) {
			this.setOwsCodeProperty(property);
			await this.refreshState(property ? { propertyOwsCode: property || '' } : undefined);
			return;
		}
		this.setFetching(false);
	}

	async setOwsCodeProperty(owsCode: string | null) {
		this.currentProperty = owsCode;
	}

	async setPreferredCurrency(currency: string) {
		if (currency != this.preferredCurrency) {
			// TODO: this should eventually handle updating the currency cookie.
			// leaving that with the UserStore for now though
			//await this.refreshState({ preferredCurrency: currency });
			this.preferredCurrency = currency;
		}
	}

	async setActiveBooking(orsConfirmationNumber: string, property?: string) {
		await this.refreshState({
			orsConfirmationNumber,
			...(property ? { propertyOwsCode: property } : {}),
		});
	}

	setActiveBookingId(bookingId: string | undefined) {
		this.activeBookingId = bookingId;
	}

	setProfile(profile: ProfileV1.ProfileSummaryResponse | undefined) {
		this.profile = profile;
	}

	setPpProfile(ppProfile: any | undefined) {
		this.ppProfile = ppProfile;
	}

	setPreferredCurrencyProperty(preferredCurrency: string) {
		this.preferredCurrency = preferredCurrency;
	}

	setFetching(fetching: boolean) {
		this.fetching = fetching;
	}

	setError(error: boolean) {
		this.error = error;
	}

	setCurrentConfirmationNumber(confirmationNumber: string | null) {
		this.currentConfirmationNumber = confirmationNumber;
	}

	setWebChatEnabled(webChatEnabled: boolean) {
		this.webChatEnabled = webChatEnabled;
	}

	setFeatureFlags(featureFlags: GlobalSettingsFeatureFlags) {
		this.featureFlags = featureFlags;
	}

	setUserPanelOpenTab(tab: UserPanelTabId) {
		this.userPanelOpenTab = tab;
	}

	clearUserPanelOpenTab() {
		this.userPanelOpenTab = null;
	}

	get userHasItinerary(): boolean {
		return Boolean(this.activeBookingId);
	}

	async refreshState(change?: Record<string, string>) {
		//check if anything actually changed
		this.setFetching(true);
		const { NEXT_PUBLIC_BFF_BASE_URL, PREFERRED_PARTNER_BFF_BASE_URL, ENVIRONMENT_NAME } =
			getConfig();

		//always include the current property if it exists, but don't overwrite it if we're changing it
		const searchParams = change || {};
		if (this.currentProperty && !searchParams['propertyOwsCode']) {
			searchParams['propertyOwsCode'] = this.currentProperty;
		}
		if (this.currentConfirmationNumber && !searchParams['orsConfirmationNumber']) {
			searchParams['orsConfirmationNumber'] = this.currentConfirmationNumber;
		}
		try {
			if (this.ppMode) {
				const data = await fetch(
					`${PREFERRED_PARTNER_BFF_BASE_URL}/preferred-partner-global-state/${change || this.currentProperty || this.currentConfirmationNumber ? `?${new URLSearchParams(searchParams)}` : ''}`,
					{
						credentials: ENVIRONMENT_NAME !== 'live' ? 'include' : 'same-origin',
					}
				).then(
					res =>
						res.json() as Promise<{
							profile: ProfileV1.PpProfileResponse | undefined;
							preferredCurrency: string;
							featureFlags: GlobalSettingsFeatureFlags;
							activeBookingId: string;
						}>
				);
				this.setActiveBookingId(data.activeBookingId);
				this.setPpProfile(data.profile);
				this.setPreferredCurrencyProperty(data.preferredCurrency);
				this.setFeatureFlags(data.featureFlags);
				this.setFetching(false);
				this.setError(false);
			} else {
				const data = await fetch(
					`${NEXT_PUBLIC_BFF_BASE_URL}/global-state/${change || this.currentProperty || this.currentConfirmationNumber ? `?${new URLSearchParams(searchParams)}` : ''}`,
					{
						credentials: ENVIRONMENT_NAME !== 'live' ? 'include' : 'same-origin',
					}
				).then(
					res =>
						res.json() as Promise<{
							activeBookingId: string | undefined;
							profile: ProfileV1.ProfileSummaryResponse | undefined;
							owsCode: string | null;
							preferredCurrency: string;
							propertyOwsCode: string;
							confirmationNumber: string;
							featureFlags: GlobalSettingsFeatureFlags;
						}>
				);
				this.setActiveBookingId(data.activeBookingId);
				this.setProfile(data.profile);
				this.setPreferredCurrencyProperty(data.preferredCurrency);
				this.setFetching(false);
				this.setError(false);
				this.setFeatureFlags(data.featureFlags);
			}
		} catch (error) {
			console.log('Error setting active booking');
			console.log(error);
			this.setError(true);
			this.setFetching(false);
			throw error;
		}
	}
}

export { GlobalStore };
